import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useSwipeable } from 'react-swipeable';

// Must match the .swipe-exit-* animation duration in App.css.
const EXIT_MS = 220;

// Must match the .swipe-back-enter animation duration in App.css (0.45s) — a
// slower, deliberate back slide that mirrors the two-stage tab transitions.
const ENTER_MS = 450;

// Cross-hook double-back guard. Module-level (shared by every swipe-back hook
// instance, per-page AND global) so a back navigation triggered by one hook
// suppresses a second one fired moments later by another. This prevents the
// per-page → global handoff double-pop, e.g. swiping back from a listing
// (PedalDetails' own hook) landing on Browse and a stray continuation firing
// Browse's now-active global hook → popping all the way to the dashboard.
const BACK_NAV_GUARD_MS = 500;
let lastBackNavAt = 0;

// A back-swipe must START within the left fraction of the screen width to
// count. Gating to a left zone keeps it from firing on ordinary horizontal
// drags over the rest of the page, while being forgiving enough to trigger.
const EDGE_RATIO = 1 / 3;

// react-swipeable only fires the L/R callbacks for a horizontal-dominant gesture,
// so vertical scrolling is unaffected. `delta` requires a deliberate drag so we
// don't flip views on small/accidental moves. trackMouse stays false because the
// feature is native-only; in the iOS Simulator a mouse click-drag registers as a
// touch, so trackTouch covers manual testing.
const COMMIT_PX = 60;
const SWIPE_OPTS = { trackTouch: true, trackMouse: false, delta: COMMIT_PX };

// ── Live-drag tuning (useLocalSwipeViews, opt-in via `liveDrag`) ─────────────
// react-swipeable's `delta` gates when onSwiping starts firing, so live drag
// needs it low enough that the pane starts moving as soon as the thumb does.
// The COMMIT_PX threshold that decides whether a gesture actually changes view
// is then enforced in the swiped handlers instead, keeping the original feel:
// a short drag shows movement and springs back, it doesn't flip the view.
const DRAG_START_PX = 12;
// The pane trails the thumb rather than matching it 1:1, and stops well short
// of the screen edge: we render one view at a time, so what's exposed behind
// the moving pane is just the page background. A short, resisted travel reads
// as "this surface moves" without opening a large empty gap.
const DRAG_RESIST = 0.55;
const DRAG_MAX_RATIO = 0.25;
// At a group edge there is no neighbouring view, so the pane barely gives.
const EDGE_DRAG_RESIST = 0.12;
// Spring back to rest when a drag ends without reaching COMMIT_PX.
const SNAP_BACK_MS = 220;
// How much a gesture must favour horizontal before it counts as a swipe rather
// than a scroll. A horizontal gesture takes over the touch completely (see the
// preventDefault in onSwiping), so mistaking a scroll for a swipe would freeze
// scrolling — the bias makes vertical the default and only hands over on a
// clearly sideways move. Between the two the axis stays undecided and the
// browser keeps scrolling until one wins.
const AXIS_BIAS = 1.5;

/**
 * The live-drag mechanics shared by all three swipe hooks: axis locking, a
 * resisted and capped offset, and the CSS custom properties the moving element
 * and any tab indicator follow.
 *
 * Deliberately imperative. touchmove fires up to once per frame, and
 * re-rendering a data-backed page that often is exactly what made the pane
 * stutter — a consumer's header alone can rebuild its toggle with fresh inline
 * styles, and none of it can be memoised away while the drag position is a
 * prop. Written straight to the DOM, the pane and the indicator both track the
 * thumb with no React render at all.
 *
 * Callers own the policy (which direction is allowed, where the edges are, what
 * counts as a commit); this owns the plumbing.
 */
function useLiveDragSurface() {
  const surfaceRef = useRef(null);
  const dragXRef = useRef(0);
  const draggingRef = useRef(false);
  const snapTimerRef = useRef(null);
  // Axis is locked on the gesture's first tracked event: a vertical scroll must
  // never start sliding the surface sideways if it later drifts horizontal.
  const axisRef = useRef(null);

  useEffect(() => () => clearTimeout(snapTimerRef.current), []);

  const setVar = (name, value) => {
    surfaceRef.current?.style.setProperty(name, value);
  };
  const setSurfaceState = (cls, on) => {
    surfaceRef.current?.classList.toggle(cls, on);
  };

  const resetAxis = () => {
    axisRef.current = null;
  };

  // Decide once per gesture whether this is a swipe or a scroll, and stay with
  // that decision even if the thumb changes direction later. A horizontal
  // gesture takes over the touch completely, so mistaking a scroll for a swipe
  // would freeze scrolling — the bias makes vertical the default and only hands
  // over on a clearly sideways move.
  const isHorizontal = (e) => {
    if (!axisRef.current) {
      if (e.absX > e.absY * AXIS_BIAS) axisRef.current = 'x';
      else if (e.absY > e.absX) axisRef.current = 'y';
    }
    if (axisRef.current !== 'x') return false;
    // A swipe owns the whole touch: stop the browser scrolling underneath it so
    // the page can't slide sideways and scroll at the same time. Depends on the
    // non-passive touchmove listener the callers configure.
    if (e.event.cancelable) e.event.preventDefault();
    return true;
  };

  // Move the surface to follow the thumb. Returns how far through the available
  // travel the gesture has come, so callers can drive an indicator from it.
  const drag = (deltaX, resist) => {
    const cap = (typeof window !== 'undefined' ? window.innerWidth : 0) * DRAG_MAX_RATIO;
    const offset = Math.max(-cap, Math.min(cap, deltaX * resist));
    // Keep subpixel precision for smoothness but drop float noise, so the
    // transform is a clean value rather than -55.00000000000001px.
    dragXRef.current = Math.round(offset * 100) / 100;
    if (!draggingRef.current) {
      draggingRef.current = true;
      clearTimeout(snapTimerRef.current);
      setSurfaceState('swipe-snapping', false);
      setSurfaceState('swipe-dragging', true);
    }
    setVar('--swipe-x', `${dragXRef.current}px`);
    return cap > 0 ? -dragXRef.current / cap : 0;
  };

  // Stop driving the live offset. `animate` eases it back to rest for a gesture
  // that didn't reach the commit threshold; a committed one cuts instantly
  // because the exit keyframes take the transform over from here.
  const endDrag = (animate, restPos) => {
    // A tap is a touch that never dragged, and it must leave the surface
    // completely alone. Falling through here would add .swipe-snapping for the
    // snap-back duration, and that class puts a transform on the pane — which
    // makes the pane the containing block for its position:fixed descendants
    // (see App.css). The pedalboard's floating Save / back-to-top buttons would
    // re-anchor from the viewport to the pane on every tap, visibly jumping and
    // landing off-screen, which also moves them out from under the finger
    // between touchend and the click — so the tap never reaches the button.
    if (!draggingRef.current) return;
    clearTimeout(snapTimerRef.current);
    draggingRef.current = false;
    dragXRef.current = 0;
    setSurfaceState('swipe-dragging', false);
    setSurfaceState('swipe-snapping', animate);
    setVar('--swipe-x', '0px');
    if (animate) {
      if (restPos !== undefined) setVar('--swipe-pos', String(restPos));
      snapTimerRef.current = setTimeout(
        () => setSurfaceState('swipe-snapping', false),
        SNAP_BACK_MS
      );
    }
  };

  // Hand the exit keyframes the offset the thumb reached, so the slide continues
  // from there instead of snapping back to centre for a frame first.
  const carryExitFrom = () => setVar('--swipe-from', `${dragXRef.current}px`);

  return {
    surfaceRef,
    dragXRef,
    draggingRef,
    setVar,
    resetAxis,
    isHorizontal,
    drag,
    endDrag,
    carryExitFrom,
  };
}

/**
 * Native-only horizontal swipe between sibling views that live in a SINGLE
 * component (local state/index). Plays a two-stage transition: the outgoing
 * (already-rendered) view slides off-screen, then the view is swapped and the
 * incoming view slides in. Sliding the outgoing view first is what makes the
 * motion visible on data-backed tabs whose incoming content is still loading.
 *
 * Apply the returned `viewClass` to the content wrapper together with a `key`
 * that changes per view (so the enter animation replays on swap). Wire the
 * surface's toggle through `transitionTo` so taps animate the same way as
 * swipes. All animation is native-only; on web `transitionTo` switches instantly
 * and `viewClass` is empty, preserving existing web behavior.
 *
 * With `liveDrag`, the content pane also tracks the thumb during the gesture
 * instead of sitting still until the swipe completes — the movement is what
 * makes the gesture discoverable at all. Apply `dragStyle` to the same wrapper
 * that carries `viewClass`, and drive the surface's own affordance (e.g. a tab
 * pill) from `tabPosition`, which is the view index as a fraction while a swipe
 * is in progress. Opt-in because a pane that visibly slides is wrong on surfaces
 * with their own horizontal drag gestures (AddPedalPage reorders images with
 * dnd-kit).
 *
 * @param {number}   index current view index
 * @param {number}   count total number of views
 * @param {(nextIndex:number)=>void} goTo caller switches to the given index
 * @param {{ liveDrag?: boolean }} [opts]
 * @returns {{ swipeProps: object, viewClass: string, transitionTo: (n:number)=>void,
 *            dragStyle: object|undefined, tabPosition: number, isDragging: boolean }}
 */
export function useLocalSwipeViews(index, count, goTo, { liveDrag = false } = {}) {
  const isNative = Capacitor.isNativePlatform();
  const prevIndex = useRef(index);
  const enterFrom =
    index === prevIndex.current ? null : index > prevIndex.current ? 'right' : 'left';
  useEffect(() => {
    prevIndex.current = index;
  }, [index]);

  const [exitDir, setExitDir] = useState(null);
  const animatingRef = useRef(false);

  const surface = useLiveDragSurface();

  // The indicator sits on the live position during a gesture and on the current
  // view at rest. Owned imperatively rather than rendered, so React never has a
  // stale idea of a value the gesture has since overwritten.
  useEffect(() => {
    if (liveDrag) surface.setVar('--swipe-pos', String(index));
  }, [index, liveDrag]); // eslint-disable-line react-hooks/exhaustive-deps

  const transitionTo = (next) => {
    if (next === index || next < 0 || next >= count) return;
    if (!isNative) {
      goTo(next);
      return;
    }
    if (animatingRef.current) return;
    animatingRef.current = true;
    if (liveDrag) {
      surface.carryExitFrom();
      surface.endDrag(false);
      // Send the indicator to its destination now: it has the whole exit slide
      // to travel, and waiting for `index` would flick it backwards first.
      surface.setVar('--swipe-pos', String(next));
    }
    // Outgoing view exits toward the side we're moving away from.
    setExitDir(next > index ? 'left' : 'right');
    setTimeout(() => {
      setExitDir(null);
      animatingRef.current = false;
      goTo(next);
    }, EXIT_MS);
  };

  const handlers = useSwipeable({
    onSwiping: (e) => {
      if (!liveDrag || !isNative) return;
      if (!surface.isHorizontal(e)) return;
      if (animatingRef.current) return;
      // deltaX is current-minus-initial, so it already carries the visual
      // direction: swiping left (toward the next view) is negative.
      const target = e.deltaX < 0 ? index + 1 : index - 1;
      const atEdge = target < 0 || target >= count;
      const progress = surface.drag(e.deltaX, atEdge ? EDGE_DRAG_RESIST : DRAG_RESIST);
      const pos = Math.min(count - 1, Math.max(0, index + progress));
      surface.setVar('--swipe-pos', String(Math.round(pos * 1000) / 1000));
    },
    // With liveDrag the library's delta is lowered so onSwiping can start early,
    // so the view-changing threshold is applied here instead.
    onSwipedLeft: (e) => {
      if (e.absX >= COMMIT_PX) transitionTo(index + 1);
    },
    onSwipedRight: (e) => {
      if (e.absX >= COMMIT_PX) transitionTo(index - 1);
    },
    onTouchStartOrOnMouseDown: () => {
      surface.resetAxis();
    },
    onTouchEndOrOnMouseUp: () => {
      surface.resetAxis();
      // A committed swipe already cleared the offset in transitionTo; only an
      // uncommitted drag needs to spring back.
      if (!liveDrag || animatingRef.current) return;
      surface.endDrag(true, index);
    },
    ...SWIPE_OPTS,
    // Non-passive touchmove is what makes the preventDefault above possible.
    // Only for liveDrag surfaces so everything else keeps a passive listener.
    ...(liveDrag ? { delta: DRAG_START_PX, touchEventOptions: { passive: false } } : null),
  });

  let viewClass = '';
  if (isNative) {
    if (exitDir) viewClass = `swipe-exit-${exitDir}`;
    else if (enterFrom) viewClass = `swipe-enter-${enterFrom}`;
  }

  // The drag variables are published on the swipe element itself, so they're
  // inherited by everything inside it — the sliding pane and the tab indicator
  // both live under it. react-swipeable delivers its listeners through a ref, so
  // ours has to be composed in here rather than handed to the caller: a second
  // `ref` on the same element would silently replace the library's and the
  // gesture would stop firing altogether.
  const attachSurface = (el) => {
    surface.surfaceRef.current = el;
    handlers.ref(el);
  };

  return {
    swipeProps: isNative ? { ...handlers, ref: attachSurface } : {},
    viewClass,
    transitionTo,
  };
}

/**
 * Native-only horizontal swipe between sibling views that are SEPARATE ROUTES
 * (e.g. Matches All/Saved/Hidden). Mirrors the local two-stage transition: the
 * current (already-rendered) page slides off-screen, then we navigate and the
 * destination slides in (it reads the travel direction from router state on
 * mount). Sliding the outgoing page first keeps the motion visible even when
 * the destination is still fetching. Scroll resets to top on switch.
 *
 * Apply the returned `viewClass` to the page's content wrapper (inside a
 * `.swipe-clip` parent). All animation is native-only; web navigates instantly.
 *
 * `liveDrag` behaves as it does in useLocalSwipeViews, with one difference worth
 * knowing: these tabs are separate route components, so the destination page
 * does not exist yet while the thumb is down. Only the outgoing page follows the
 * thumb, and what's exposed behind it is the page background rather than the
 * next tab. That's the same trade the local hook already makes (it renders one
 * view at a time too), which is why the travel is short and resisted.
 *
 * @param {{ to: string }[]} tabs ordered routes
 * @param {string} currentPath location.pathname
 * @param {{ liveDrag?: boolean }} [opts]
 * @returns {{ swipeProps: object, viewClass: string }}
 */
export function useRouteSwipeViews(tabs, currentPath, { liveDrag = false } = {}) {
  const isNative = Capacitor.isNativePlatform();
  const navigate = useNavigate();
  const location = useLocation();
  const index = Math.max(
    0,
    tabs.findIndex((t) => currentPath === t.to || currentPath.startsWith(`${t.to}/`))
  );

  const [exitDir, setExitDir] = useState(null);
  const animatingRef = useRef(false);
  const surface = useLiveDragSurface();

  useEffect(() => {
    if (liveDrag) surface.setVar('--swipe-pos', String(index));
  }, [index, liveDrag]); // eslint-disable-line react-hooks/exhaustive-deps

  // exitToward = side the current page slides out to; swipeDir = side the new
  // page enters from (handed to the destination via router state).
  const go = (next, exitToward, swipeDir) => {
    if (next < 0 || next >= tabs.length) return;
    if (!isNative) {
      navigate(tabs[next].to);
      window.scrollTo(0, 0);
      return;
    }
    if (animatingRef.current) return;
    animatingRef.current = true;
    if (liveDrag) {
      surface.carryExitFrom();
      surface.endDrag(false);
      surface.setVar('--swipe-pos', String(next));
    }
    setExitDir(exitToward);
    setTimeout(() => {
      navigate(tabs[next].to, { state: { swipeDir } });
      window.scrollTo(0, 0);
    }, EXIT_MS);
  };

  const handlers = useSwipeable({
    onSwiping: (e) => {
      if (!liveDrag || !isNative) return;
      if (!surface.isHorizontal(e)) return;
      if (animatingRef.current) return;
      const target = e.deltaX < 0 ? index + 1 : index - 1;
      const atEdge = target < 0 || target >= tabs.length;
      const progress = surface.drag(e.deltaX, atEdge ? EDGE_DRAG_RESIST : DRAG_RESIST);
      const pos = Math.min(tabs.length - 1, Math.max(0, index + progress));
      surface.setVar('--swipe-pos', String(Math.round(pos * 1000) / 1000));
    },
    // liveDrag lowers the library's delta so the page starts moving early, so the
    // threshold that actually changes tab is enforced here instead.
    onSwipedLeft: (e) => {
      if (!liveDrag || e.absX >= COMMIT_PX) go(index + 1, 'left', 'right');
    },
    onSwipedRight: (e) => {
      if (!liveDrag || e.absX >= COMMIT_PX) go(index - 1, 'right', 'left');
    },
    onTouchStartOrOnMouseDown: () => {
      surface.resetAxis();
    },
    onTouchEndOrOnMouseUp: () => {
      surface.resetAxis();
      if (!liveDrag || animatingRef.current) return;
      surface.endDrag(true, index);
    },
    ...SWIPE_OPTS,
    ...(liveDrag ? { delta: DRAG_START_PX, touchEventOptions: { passive: false } } : null),
  });

  let viewClass = '';
  if (isNative) {
    const enterDir = location.state?.swipeDir;
    if (exitDir) viewClass = `swipe-exit-${exitDir}`;
    else if (enterDir === 'right' || enterDir === 'left') viewClass = `swipe-enter-${enterDir}`;
  }

  const attachSurface = (el) => {
    surface.surfaceRef.current = el;
    handlers.ref(el);
  };

  return {
    swipeProps: isNative ? { ...handlers, ref: attachSurface } : {},
    viewClass,
  };
}

/**
 * Native-only iOS-style "swipe back": a right-swipe that begins at the left
 * screen edge invokes `onBack` (typically `() => navigate(-1)`). Edge-gating
 * keeps it from competing with horizontal content. Spread the returned props on
 * the page's root element. Returns {} on web (no gesture) and when `enabled` is
 * false (e.g. while a fullscreen image overlay is open).
 *
 * Two animation styles:
 *  - 'exit' (default, per-page use): the current page slides off to the right,
 *    THEN we navigate. Safe because the page component unmounts on navigation,
 *    which clears the `forwards`-filled transform.
 *  - 'enter' (global app-level use): we navigate FIRST, then slide the
 *    destination in from the left. Required for the global wrapper, which never
 *    unmounts across routes — a lingering `swipe-exit-right` (forwards fill)
 *    would stick and blank every subsequent page. `swipe-enter-left` ends at
 *    translateX(0), so it can never get stuck off-screen.
 *
 * `liveDrag` lets the page follow the thumb during the gesture instead of
 * sitting still until it completes. It is honoured ONLY for animation 'exit',
 * i.e. per-page use where the component unmounts on navigation. The 'enter'
 * variant runs on App.js's persistent shell, which never unmounts across routes,
 * and leaving a live transform on that element would offset every subsequent
 * page — the same trap the animation modes themselves exist to avoid.
 *
 * @param {() => void} onBack invoked on a valid back-swipe
 * @param {{ enabled?: boolean, edgeGate?: boolean, delta?: number, animation?: 'exit'|'enter'|'none', liveDrag?: boolean }} [opts]
 * @returns {object} props to spread on the page root
 */
export function useEdgeSwipeBack(
  onBack,
  { enabled = true, edgeGate = true, delta = 28, animation = 'exit', liveDrag = false } = {}
) {
  const isNative = Capacitor.isNativePlatform();
  const [exiting, setExiting] = useState(false);
  const [entering, setEntering] = useState(false);
  const animatingRef = useRef(false);
  const surface = useLiveDragSurface();
  const dragging = liveDrag && animation === 'exit';

  // Safety net: if we navigate away mid-animation, make sure the body clip class
  // doesn't linger on the next screen.
  useEffect(() => () => document.body.classList.remove('native-swiping-back'), []);

  // Whether the gesture started inside the left-edge zone. Shared by onSwiping
  // and onSwipedRight so the live drag can't move a page that the commit would
  // then refuse to navigate.
  const startedAtEdge = (e) => {
    if (!edgeGate) return true;
    const startX = Array.isArray(e.initial) ? e.initial[0] : Infinity;
    const edgeZone = (typeof window !== 'undefined' ? window.innerWidth : 0) * EDGE_RATIO;
    return startX <= edgeZone;
  };

  // Element-level opt-out: a gesture born inside a container marked
  // data-swipe-back-opt-out belongs to that container, not to navigation.
  // Route-level opt-outs (SWIPE_BACK_OPT_OUT_PREFIXES in App.js) cost a whole
  // page its back gesture, which is wrong for drill-in pages that merely
  // contain one scrollable widget. Touch event targets stay pinned to the
  // element the finger went down on, so closest() on the target answers "did
  // this gesture START inside the marked box" even at commit time. First
  // needed for the Trade Offer form's additional-pedals grid on /match and
  // /offer, where an arced thumb-scroll in the vertically scrolling box read
  // as a rightward swipe and navigated away mid-selection (2026-10-02).
  // Limitation: native listeners never see events from portaled children, so
  // the marker only protects plain descendants of the swipe surface.
  const startedInOptOut = (e) => {
    const target = e?.event?.target;
    return !!(target && typeof target.closest === 'function' && target.closest('[data-swipe-back-opt-out]'));
  };

  const handlers = useSwipeable({
    onSwiping: (e) => {
      if (!dragging || !isNative || !enabled) return;
      if (animatingRef.current) return;
      // Back is a rightward gesture only; a leftward drag must not move the page.
      if (e.deltaX <= 0) return;
      if (!startedAtEdge(e)) return;
      // Before the drag starts moving the page, so an ignored gesture never
      // adds the body clip class that would then need unwinding.
      if (startedInOptOut(e)) return;
      if (!surface.isHorizontal(e)) return;
      // Sliding the page right opens a gap on the left; clip the body so that
      // gap can't be scrolled to or add a horizontal scrollbar mid-gesture.
      if (!surface.draggingRef.current) document.body.classList.add('native-swiping-back');
      surface.drag(e.deltaX, DRAG_RESIST);
    },
    onTouchStartOrOnMouseDown: () => {
      surface.resetAxis();
    },
    onTouchEndOrOnMouseUp: () => {
      surface.resetAxis();
      // A committed swipe keeps the clip for its exit animation and clears the
      // offset itself; only an uncommitted drag unwinds here.
      if (!dragging || animatingRef.current) return;
      document.body.classList.remove('native-swiping-back');
      surface.endDrag(true);
    },
    onSwipedRight: (e) => {
      // Gate at FIRE time, not attach time. react-swipeable wires its touch
      // listeners through a ref callback that IGNORES null (no detach). The
      // global back-swipe lives on App.js's persistent shell div, which never
      // unmounts across routes — so returning {} when `enabled` flips false
      // does NOT remove the already-attached listeners. They kept firing
      // navigate(-1) on opted-out pages (dashboard, matches, add pedal,
      // my pedals), hijacking every right-swipe that should have been a tab
      // toggle. Checking `enabled` here uses the CURRENT render's value
      // (react-swipeable re-reads its options object on every event).
      if (!enabled) return;
      if (animatingRef.current) return;
      // Edge gate: only used by per-page swipe-backs that share the screen with
      // horizontal image content, so the gesture must start at the left edge.
      // The global back-swipe disables it (edgeGate:false) to match the Add
      // Pedal/Want page — swipe from anywhere — which the user finds reliable.
      if (!startedAtEdge(e)) return;
      // Before the back-nav guard timestamp, so an ignored swipe doesn't
      // consume the guard window and shadow a legitimate one moments later.
      if (startedInOptOut(e)) return;
      // With liveDrag the library's delta is lowered so the page starts moving
      // early, so the threshold that actually navigates is enforced here.
      if (dragging && e.absX < COMMIT_PX) return;

      // Suppress a back-nav that arrives right behind another (see guard note).
      const now = Date.now();
      if (now - lastBackNavAt < BACK_NAV_GUARD_MS) return;
      lastBackNavAt = now;

      if (animation === 'none') {
        onBack();
        return;
      }

      if (animation === 'enter') {
        // Navigate first, then slide the destination in from the left. Clip the
        // body during the slide so the off-screen (-100%) start doesn't expose a
        // horizontal scrollbar.
        animatingRef.current = true;
        document.body.classList.add('native-swiping-back');
        onBack();
        setEntering(true);
        setTimeout(() => {
          setEntering(false);
          document.body.classList.remove('native-swiping-back');
          animatingRef.current = false;
        }, ENTER_MS);
        return;
      }

      // 'exit' (default): slide the current page off-screen, then navigate.
      animatingRef.current = true;
      document.body.classList.add('native-swiping-back');
      if (dragging) {
        // Continue the slide from where the thumb let go rather than jumping
        // back to centre for a frame first.
        surface.carryExitFrom();
        surface.endDrag(false);
      }
      setExiting(true);
      setTimeout(onBack, EXIT_MS);
    },
    trackTouch: true,
    trackMouse: false,
    delta: dragging ? DRAG_START_PX : delta,
    ...(dragging ? { touchEventOptions: { passive: false } } : null),
  });

  let exitClass = '';
  if (isNative) {
    if (exiting) exitClass = 'swipe-exit-right';
    // Dedicated slower enter + leading-edge shadow (visible on all-cream pages).
    else if (entering) exitClass = 'swipe-back-enter swipe-back-shadow';
  }

  const attachSurface = (el) => {
    surface.surfaceRef.current = el;
    handlers.ref(el);
  };

  return {
    // Always keep the handlers spread on native. Withholding them when
    // disabled is exactly the trap described above: the ref-attached listeners
    // survive on persistent elements anyway, but with a STALE options object.
    // Keeping the spread ensures react-swipeable sees the current `enabled`
    // (checked inside onSwipedRight), which is the gate that actually works.
    swipeProps: isNative ? (dragging ? { ...handlers, ref: attachSurface } : handlers) : {},
    exitClass,
    // Unlike the tab hubs, where an inner pane moves inside a stationary surface,
    // the back-swipe surface is itself the thing that slides. This marker is what
    // tells the CSS to transform this element rather than a child pane. Returned
    // for the caller to render rather than added imperatively, because the
    // caller's className is a template that React rewrites when exitClass
    // changes — which would silently drop a class we had set on the DOM.
    dragClass: isNative && dragging ? 'swipe-drag-self' : '',
  };
}
